import { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { PortalModal, Field } from "@/components/portal/PortalModal";
import { useDialogs } from "@/lib/dialogs";
import { MODULES, PLATFORM, isConnected, buildPlatformUrl } from "@/config/platform";

const openPath = (path) => {
  try {
    window.open(buildPlatformUrl(path), "_blank", "noopener,noreferrer");
    toast.success("Opened the existing application in a new tab. Sign-in and access are enforced there.");
  } catch {
    toast.error("The configured platform URL is invalid.");
  }
};

const EventIdForm = ({ module, onBack }) => {
  const [id, setId] = useState("");
  const [err, setErr] = useState("");
  const go = () => {
    if (!/^[a-zA-Z0-9_-]{3,128}$/.test(id.trim())) { setErr("Enter a valid existing event ID."); return; }
    openPath(module.route.replace(/:eid|:eventId/g, encodeURIComponent(id.trim())));
  };
  return (
    <div data-testid="launcher-event-id-form">
      <p>{module.name} uses an event record ID from the existing application. No event record is created here.</p>
      <Field id="launcher-event-id" label="Existing event ID" error={err}>
        <input id="launcher-event-id" value={id} onChange={(e) => { setId(e.target.value); setErr(""); }} autoComplete="off" placeholder="Paste the ID from the existing app" data-testid="launcher-event-id-input" />
      </Field>
      <div className="notice">The existing application authenticates you and enforces tenant-scoped authorization. Ayndri never passes tokens or tenant IDs in the URL.</div>
      <div className="modal-actions">
        <button className="btn outline" onClick={onBack} data-testid="launcher-event-id-back-button">Back</button>
        <button className="btn blue" onClick={go} data-testid="launcher-event-id-open-button">Open existing module</button>
      </div>
    </div>
  );
};

export const LauncherDialog = ({ onClose }) => {
  const open = useDialogs();
  const [pending, setPending] = useState(null);
  const connected = isConnected();

  const launch = (m) => {
    if (!connected) { toast.warning(`Demo mode: the platform URL is not configured, so ${m.name} cannot open yet.`); return; }
    if (m.needsEventId) { setPending(m); return; }
    openPath(m.route);
  };

  return (
    <PortalModal title="Existing Event Management — Application Launcher" wide brand onClose={onClose} testId="launcher-dialog">
      {pending ? <EventIdForm module={pending} onBack={() => setPending(null)} /> : (
        <>
          <div className="notice"><b>This is not a replacement application.</b> Every tile opens the canonical module in {PLATFORM.repo}. CRM, ERP, sourcing, Event Graph, approvals and EventThreads stay there. Visibility per module is enforced by the platform's own route guards and entitlements.</div>
          <div className="launcher-bar">
            <span className={`tag ${connected ? "greentag" : "graytag"}`} data-testid="launcher-connection-status">{connected ? "Platform URL configured · sign-in happens in the existing app" : "Demo mode · not connected"}</span>
            <button className="btn outline small" onClick={() => open("connect")} data-testid="launcher-settings-button"><Icons.Settings className="ic" /> Integration status</button>
          </div>
          <div className="appgrid">
            {MODULES.map((m) => {
              const Icon = Icons[m.icon] || Icons.Square;
              return (
                <button key={m.key} className="apptile" onClick={() => launch(m)} data-testid={`launcher-tile-${m.key}`}>
                  <Icon className="ic" /><b>{m.name}</b><span>{m.desc}</span>
                  <small className="tile-route">{m.route} {m.verified ? "· verified route" : "· UNVERIFIED route"}{m.cap ? ` · needs ${m.cap}` : ""}</small>
                </button>
              );
            })}
          </div>
          <p className="fineprint">Routes were verified against the existing application's router. Access is still enforced there by sign-in and role capabilities. No API keys, passwords or tenant sessions are stored by this portal.</p>
        </>
      )}
    </PortalModal>
  );
};
